import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Card as CardMUI, Button, Grid, Typography, styled } from '@mui/material';

import { LocationOn } from '@mui/icons-material';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';

import NotificationIcon from 'assets/img/icons/vehicle-tracking.svg';
import { TelemetryActions } from 'state/modules/telemetry';
import { EditTelemetryModal } from 'components/Modals';
import TelemetryStats from './TelemetryStats';
import TelemetryMap from './TelemetryMap';
import Loading from 'components/Loading';
import { Card } from 'components/UI';
import { useConfirm } from 'hooks';
import { capitalize } from 'utils';
import moment from 'moment';
import i18n from 'i18n-js';
import Can from 'components/Can';
import PropTypes from 'prop-types';

const centerFallback = { lat: 34.05, lng: -118.3 };

const Timezone = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const BoxContainer = styled(Box)({
  marginBottom: 5,
  padding: 20,
});

const StyledGpsIcon = styled(GpsFixedIcon)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginRight: 10,
}));

const NotificationContainer = styled(Box)({
  justifyContent: 'center',
  alignItems: 'center',
  position: 'absolute',
  display: 'flex',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
});

const NotificationContent = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  borderRadius: '8px',
  boxShadow: theme.shadows[4],
  background: '#FFFFFF',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  maxWidth: '60%',
  padding: '2em 1em 1em',
  position: 'relative',
  width: '100%',
}));

const StyledNotificationIcon = styled('img')({
  width: '2.4em',
});

const NotificationTitle = styled('p')(({ theme }) => ({
  color: theme.palette.primary.lightGray,
  fontSize: theme.typography.pxToRem(14),
  fontWeight: '700',
}));

const NotificationSubtitle = styled('p')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.fontSize,
  margin: '10px 0',
  textAlign: 'center',
}));

const LoadingContainer = styled(Box)({
  backgroundColor: 'rgba(255, 255, 255, 0.75)',
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
});

export const DatetimeField = ({ date, localeFormat, timezone = moment.tz.guess() }) => {
  const time = timezone ? moment(date).tz(timezone) : moment(date);
  const timezoneFormat = (
    <Timezone>
      ({timezone ? moment().tz(timezone).format('UTC Z') : moment().format('UTC Z')})
    </Timezone>
  );

  return (
    <>
      {' '}
      {time.format(`${localeFormat} hh:mm A`)} {timezoneFormat}{' '}
    </>
  );
};

DatetimeField.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date), PropTypes.number])
    .isRequired,
  localeFormat: PropTypes.string.isRequired,
  timezone: PropTypes.string,
};

const Telemetry = ({ vehicleId, timezone }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { localeFormat } = useSelector((state) => state.organizations);
  const [toggleBlockedData, ShowBlockedDataConfirm] = useConfirm({
    title: capitalize(i18n.t('telemetry.load_data')),
    message: i18n.t('telemetry.agree_text'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('telemetry.continue'),
    onConfirm: () => {
      setAcceptTerms(true);
      updateGpsLocation(true);
    },
  });

  const { data, loading } = useSelector((state) => state.telemetry);

  const loadTelemetry = useCallback(
    (params = {}) => {
      dispatch(TelemetryActions.fetchTelemetry(vehicleId, params));
    },
    [dispatch, vehicleId]
  );

  const updateGpsLocation = (terms) => {
    let params = {};
    if (terms || acceptTerms) {
      params.agree_terms = true;
    }
    loadTelemetry(params);
  };

  const handleModalOpen = () => {
    setOpen((prevState) => !prevState);
  };

  useEffect(() => {
    loadTelemetry();
  }, [loadTelemetry]);

  const items = [
    {
      title: i18n.t('telemetry.edit'),
      onClick: handleModalOpen,
    },
    {
      title: i18n.t('telemetry.update_gps'),
      onClick: updateGpsLocation,
    },
  ];

  const distanceToLocationValue = data?.distance_to_location?.magnitude?.toString() || '';
  const distanceToLocationUnit = data?.distance_to_location?.units || '';
  const distanceToLocation = distanceToLocationValue
    ? `${distanceToLocationValue} ${distanceToLocationUnit}`
    : null;
  const showLocationCard =
    data?.distance_to_location || data.gps_coordinates || data?.gps_blocked ? true : false;

  const renderLocationCard = () => {
    return (
      <CardMUI>
        <Box position="relative">
          <BoxContainer style={{ filter: data.gps_blocked ? 'blur(5px)' : 'none' }}>
            <Box
              sx={{
                mb: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Grid container>
                <Grid size={{ xs: 4 }} sx={{ p: 1 }} data-cy="location-title">
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ fontWeight: 500 }}>
                    {i18n.t('common.location')}{' '}
                  </Typography>
                </Grid>
                {data?.gps_time && (
                  <Grid size={{ xs: 8 }} sx={{ p: 1 }}>
                    <Box width="100%" display="flex" justifyContent="flex-end">
                      <Typography variant="caption">
                        <Box>
                          {i18n.t('telemetry.time_label')}{' '}
                          <DatetimeField
                            timezone={timezone}
                            date={data.gps_time}
                            localeFormat={localeFormat}
                          />
                        </Box>
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>

            {distanceToLocation && (
              <Grid container>
                <Grid size={{ xs: 8 }} sx={{ p: 1 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <LocationOn
                      sx={{
                        color: '#387FF7',
                        mr: '10px',
                      }}
                    />
                    <Typography variant="subtitle2">
                      {i18n.t('telemetry.distance_location')}:
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 4 }} sx={{ p: 1 }}>
                  <Box>
                    <Typography variant="caption">
                      <Box fontSize={13} display="flex" justifyContent="flex-end">
                        <span style={{ fontWeight: 400 }}> {distanceToLocation}</span>
                      </Box>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}

            <Grid container style={{ marginBottom: 30 }}>
              <Grid size={{ xs: 8 }} sx={{ p: 1 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <StyledGpsIcon />
                  <Typography variant="subtitle2">
                    {i18n.t('telemetry.gps')}: {i18n.t('telemetry.gps_coordinates')}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 4 }} sx={{ p: 1 }}>
                <Box>
                  {data?.gps_coordinates && (
                    <Box fontSize={13} display="flex" justifyContent="flex-end">
                      <Typography variant="caption" sx={{ textAlign: 'right' }}>
                        <span style={{ fontWeight: 400 }}>
                          {data.gps_coordinates.lat}, {data.gps_coordinates.lng}
                        </span>
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>

            {renderMap(data?.gps_coordinates)}
          </BoxContainer>

          {data.gps_blocked && (
            <NotificationContainer>
              <NotificationContent>
                <StyledNotificationIcon src={NotificationIcon} alt="notification-icon" />
                <NotificationTitle>{i18n.t('telemetry.restricted_access')}</NotificationTitle>
                <NotificationSubtitle>{i18n.t('telemetry.data_hidden')}</NotificationSubtitle>
                <Can I="read_inprogress" a="telemetry">
                  <Button onClick={toggleBlockedData} variant="outlined" color="primary">
                    {i18n.t('telemetry.load_data')}
                  </Button>
                </Can>
              </NotificationContent>
            </NotificationContainer>
          )}
        </Box>
      </CardMUI>
    );
  };

  const renderMap = (coordinates) => {
    if (!data.gps_coordinates && !data.gps_blocked) {
      return (
        <Typography variant="h3" color="textPrimary">
          <Box fontSize={13} align="center">
            {i18n.t('telemetry.no_gps_location')}
          </Box>
        </Typography>
      );
    }

    const hasShowableCoordinates = data.gps_coordinates && !data.gps_blocked;

    return (
      <TelemetryMap
        offsetLat={0}
        locations={[
          {
            lat: hasShowableCoordinates ? coordinates.lat : centerFallback.lat,
            lng: hasShowableCoordinates ? coordinates.lng : centerFallback.lng,
            icon: 'pickup',
          },
        ]}
      />
    );
  };

  return (
    <Card
      menuItems={items}
      data-cy="telemetry"
      headerActionType="vertical"
      title={i18n.t('telemetry.title')}
    >
      <Box>
        {loading ? (
          <Box position="relative">
            <LoadingContainer>
              <Loading />
            </LoadingContainer>
          </Box>
        ) : (
          <>
            <TelemetryStats
              telemetry={data}
              timezone={timezone}
              onUpdate={handleModalOpen}
              localeFormat={localeFormat}
            />
            {showLocationCard && renderLocationCard()}
          </>
        )}

        {open && <EditTelemetryModal open={open} telemetry={data} handleClick={handleModalOpen} />}
        <ShowBlockedDataConfirm />
      </Box>
    </Card>
  );
};

Telemetry.propTypes = {
  vehicleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  timezone: PropTypes.string,
};

export default Telemetry;
